import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project-imports
import { APP_DEFAULT_PATH } from 'config';

// types
import { GuardProps } from 'types/auth';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { useAllowedControllers } from 'utils/menu/accessController';
import { useLazyMeQuery } from 'api/auth';

// ==============================|| GUEST GUARD ||============================== //

const GuestGuard = ({ children }: GuardProps) => {
  const authToken = useSelector((store: RootState) => store.auth.authToken);
  const navigate = useNavigate();
  const location = useLocation();
  const user_id = localStorage.getItem('user_id');
  const pathname = localStorage.getItem('last_location');
  const decodedAuthToken = useSelector((state: RootState) => state.auth.decodedAuthToken);
  const allowedControllers = useAllowedControllers();
  const [init, setIsInit] = useState<boolean>(false);
  const [fetchMeInfo] = useLazyMeQuery();

  const initToken = useCallback(async () => {
    if (!init && !decodedAuthToken) {
      try {
        await fetchMeInfo().unwrap();
      } catch (error) {
        if (error) {
          navigate('/login', {
            state: {
              from: location.pathname
            },
            replace: true
          });
        }
      }
      setIsInit(true);
    }
  }, [init, fetchMeInfo, navigate, location.pathname, decodedAuthToken]);

  useEffect(() => {
    initToken();
  }, [initToken]);

  const formatController = (controller: string) => {
    const [first, ...rest] = controller.split('-');
    return `/${first}/${rest.join('-')}`;
  };

  const normalizedAllowedControllers = allowedControllers.map(formatController);

  const getBasePath = (path: string) => path.split('/').slice(0, 3).join('/');
  const dashboardRoutes = ['/dashboard/admin', '/dashboard/process', '/dashboard/qa-manager'];
  const documentSettingRoutes = [
    '/design/document-settings',
    '/design/document-upload',
    '/design/data-point-configuration',
    '/design/document-configuration'
  ];

  useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (decodedAuthToken && localStorage.getItem('isLoggedOut') != 'true') {
      if (location.pathname !== '/reset-password') {
        const fromPath = getBasePath(location.state?.from || '');

        const hasDocumentListAccess = normalizedAllowedControllers.includes('/design/document-list');
        const isDocumentSettingRoute = documentSettingRoutes.includes(fromPath);

        if (
          user_id === decodedAuthToken?.nameid &&
          (normalizedAllowedControllers.includes(fromPath) ||
            fromPath === '/dashboard' ||
            (hasDocumentListAccess && isDocumentSettingRoute))
        ) {
          navigate(location.state.from, {
            state: { from: '' },
            replace: true
          });
        } else {
          const order = ['/dashboard', '/design', '/training', '/review', '/report', '/admin'];

          const groupedRoutes = normalizedAllowedControllers.reduce((acc, route) => {
            const firstPart: any = route.split('/').slice(0, 2).join('/');
            if (!acc[firstPart]) acc[firstPart] = [];
            acc[firstPart].push(route);
            return acc;
          }, {} as any);

          const orderedRoutes = order.flatMap((section) => groupedRoutes[section] || []);
          const hasDashboardRoute = dashboardRoutes.some((route) => normalizedAllowedControllers.includes(route));
          const path = orderedRoutes[0];
          const firstPart = path.split('/').slice(0, 2).join('/');
          navigate(hasDashboardRoute ? APP_DEFAULT_PATH : firstPart, {
            state: { from: '' },
            replace: true
          });
        }
      }
    } else if (location.pathname.slice(1).length <= 0 && !decodedAuthToken) {
      navigate('/login');
    }
  }, [authToken, navigate, location, user_id, decodedAuthToken, pathname, normalizedAllowedControllers]);

  return children;
};

export default GuestGuard;
